import type {
	EntityPatch,
	HistoryDomainAdapter,
	HistoryEntitySnapshot,
	HistoryPatchApplyInput,
} from '..';
import {
	isPlainSketchEntityReferenceHistorySnapshot,
	type SketchEntityReferenceHistorySnapshot,
} from '../../reference/sketch-entity-reference/history/SketchEntityReferenceHistorySnapshot';
import type {SketchEntityReferenceRecord} from '../../reference/sketch-entity-reference/model/SketchEntityReferenceRecord';

/**
 * 草图实体引用历史 adapter ID。
 */
export const SKETCH_ENTITY_REFERENCE_HISTORY_ADAPTER_ID = 'history-domain:sketch-entity-reference';

/**
 * 草图实体引用历史同步存储。
 *
 * 设计说明：
 * 1. `PatchApplier` 是同步 apply / rollback 内核，adapter 不能把 Promise 写入丢到后台；
 * 2. 上层需要在进入历史系统前把引用仓储、目标实体 tag 重绑等动作适配成同步端口；
 * 3. 若同步端口抛错，`PatchApplier` 才能阻止 history cursor 前进并回滚已应用补丁。
 */
export interface SketchEntityReferenceHistoryStore {
	/**
	 * 恢复引用记录。
	 *
	 * 设计说明：该端口应同步完成引用记录写入与目标实体 tag 重绑，避免历史 adapter
	 * 在同一个 patch 内拆成多次副作用后无法由 `PatchApplier` 回滚当前 patch。
	 *
	 * @param input 恢复输入，包含目标引用与恢复前引用。
	 */
	readonly restoreReference: (input: SketchEntityReferenceHistoryStoreRestoreInput) => void;
	/**
	 * 删除引用记录。
	 *
	 * @param input 删除输入，包含引用 ID 与删除前引用。
	 */
	readonly deleteReference: (input: SketchEntityReferenceHistoryStoreDeleteInput) => void;
}

/**
 * 引用历史恢复输入。
 */
export interface SketchEntityReferenceHistoryStoreRestoreInput {
	/** 需要恢复的引用记录。 */
	readonly reference: SketchEntityReferenceRecord;
	/** 恢复前的引用记录；不存在时为 null。 */
	readonly previousReference: SketchEntityReferenceRecord | null;
}

/**
 * 引用历史删除输入。
 */
export interface SketchEntityReferenceHistoryStoreDeleteInput {
	/** 引用记录 ID。 */
	readonly referenceId: string;
	/** 删除前的引用记录；不存在时为 null。 */
	readonly previousReference: SketchEntityReferenceRecord | null;
}

/**
 * 从历史快照中读取 plain 引用记录。
 *
 * @param snapshot 历史实体快照。
 * @param entityId 引用记录 ID。
 * @returns 存在时返回引用记录，否则返回 null。
 */
function resolveReferenceFromSnapshot(
	snapshot: HistoryEntitySnapshot<SketchEntityReferenceHistorySnapshot>,
	entityId: string,
): SketchEntityReferenceRecord | null {
	if (!snapshot.exists) {
		return null;
	}

	if (!snapshot.data) {
		throw new Error(`草图实体引用 ${entityId} 缺少快照数据。`);
	}

	if (!isPlainSketchEntityReferenceHistorySnapshot(snapshot.data)) {
		throw new Error(`草图实体引用 ${entityId} 的快照不是可序列化 plain data。`);
	}

	if (snapshot.data.referenceId !== entityId) {
		throw new Error(`草图实体引用补丁 ID 不一致：expected=${entityId}, actual=${snapshot.data.referenceId}`);
	}

	return snapshot.data.reference;
}

/**
 * 解析应用前引用记录。
 *
 * @param input 补丁应用输入。
 * @returns 应用前引用记录；不存在时为 null。
 */
function resolvePreviousReference(
	input: HistoryPatchApplyInput<
		SketchEntityReferenceHistorySnapshot,
		SketchEntityReferenceHistoryStore
	>,
): SketchEntityReferenceRecord | null {
	const previousSnapshot = input.direction === 'undo' ? input.patch.after : input.patch.before;
	return resolveReferenceFromSnapshot(previousSnapshot, input.patch.entityId);
}

/**
 * 应用草图实体引用快照。
 *
 * @param input 补丁应用输入。
 */
function applySketchEntityReferenceSnapshot(
	input: HistoryPatchApplyInput<
		SketchEntityReferenceHistorySnapshot,
		SketchEntityReferenceHistoryStore
	>,
): void {
	const snapshot = input.snapshot;
	const previousReference = resolvePreviousReference(input);

	if (!snapshot.exists) {
		input.context.deleteReference({
			referenceId: input.patch.entityId,
			previousReference,
		});

		return;
	}

	const reference = resolveReferenceFromSnapshot(snapshot, input.patch.entityId);
	if (!reference) {
		throw new Error(`草图实体引用 ${input.patch.entityId} 缺少可恢复快照。`);
	}

	input.context.restoreReference({
		reference,
		previousReference,
	});
}

/**
 * 比较草图实体引用快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 快照语义相等时返回 true。
 */
function areSketchEntityReferenceSnapshotsEqual(
	left: HistoryEntitySnapshot<SketchEntityReferenceHistorySnapshot>,
	right: HistoryEntitySnapshot<SketchEntityReferenceHistorySnapshot>,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建草图实体引用历史 adapter。
 *
 * @returns 草图实体引用历史 adapter。
 */
export function createSketchEntityReferenceHistoryAdapter(): HistoryDomainAdapter<
	SketchEntityReferenceHistorySnapshot,
	SketchEntityReferenceHistoryStore
> {
	return {
		adapterId: SKETCH_ENTITY_REFERENCE_HISTORY_ADAPTER_ID,
		kind: 'sketch-entity-reference',
		applySnapshot: applySketchEntityReferenceSnapshot,
		areSnapshotsEqual: areSketchEntityReferenceSnapshotsEqual as HistoryDomainAdapter<
			SketchEntityReferenceHistorySnapshot,
			SketchEntityReferenceHistoryStore
		>['areSnapshotsEqual'],
	};
}

/**
 * 创建草图实体引用历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 草图实体引用历史补丁。
 */
export function createSketchEntityReferenceHistoryPatch(
	patch: Omit<EntityPatch<SketchEntityReferenceHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<SketchEntityReferenceHistorySnapshot> {
	return {
		...patch,
		entityKind: 'sketch-entity-reference',
		adapterId: SKETCH_ENTITY_REFERENCE_HISTORY_ADAPTER_ID,
	};
}
